import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getSoftwareDetail } from '@/lib/api';
import { PageHero, Section, SectionTitle } from '@/components/shared/Section';
import { MiniForm } from '@/components/shared/Forms';
import { ShieldCheck, ArrowRight, CheckCircle, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { fadeUp } from '@/lib/motion';

export default function SoftwarePage() {
  const { slug } = useParams();
  const [sw, setSw] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getSoftwareDetail(slug).then(r => { setSw(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0EA5E9] border-t-transparent rounded-full animate-spin" /></div>;
  if (!sw) return <div className="min-h-screen flex items-center justify-center text-slate-500">Software no encontrado</div>;

  return (
    <div>
      <PageHero badge="SOFTWARE PROPIO" badgeIcon={ShieldCheck} h1={sw.name} subtitle={sw.description}
        primaryCTA={<Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">Ver demo <ArrowRight className="w-4 h-4" /></Link>}
        secondaryCTA={<a href="https://wa.me/56975294340" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
      />
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="bg-[#0A2540] rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-4"><div className="w-2.5 h-2.5 rounded-full bg-red-400" /><div className="w-2.5 h-2.5 rounded-full bg-yellow-400" /><div className="w-2.5 h-2.5 rounded-full bg-green-400" /><span className="ml-2 text-[10px] text-slate-500 font-mono">ALLCOM {sw.name}</span></div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">{[1,2,3].map(i => <div key={i} className="h-20 rounded-lg bg-white/5" />)}</div>
                <div className="h-40 rounded-lg bg-white/5" />
                <div className="grid grid-cols-2 gap-2">{[1,2].map(i => <div key={i} className="h-12 rounded-lg bg-white/5" />)}</div>
              </div>
            </div>
          </div>
          <motion.div variants={fadeUp}>
            <SectionTitle title="Características principales" subtitle="Módulos y funcionalidades incluidas en este producto." />
            <ul className="space-y-3">
              {sw.features?.map((f, i) => (
                <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </Section>
      <Section alt>
        <div className="max-w-xl mx-auto">
          <SectionTitle title="Agenda una demo" subtitle="Ve este software en acción con datos reales de tu industria." center />
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg">
            <MiniForm sourcePage={`/software/${slug}`} />
          </div>
        </div>
      </Section>
    </div>
  );
}
